import _ from 'lodash';
import moment from 'moment';

import React, {
    useContext
} from 'react';

import {
    useIsExportingPPT,
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import {
    useTranslation
} from '../../../../i18n.js';

import LandscapeContext from '../../../contexts/landscapeContext.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';
import Panel from '../../../components/Panel.jsx';
import Label from '../../../components/Label.jsx';
import RIQMetricLineChart from '../../../components/RIQMetricLineChart.jsx';

import {
    RIQPopover
} from '../../../components/RIQPopover.jsx';

import {
    DeltaRelativeInsight,
    NumericalValue,
    Summary
} from '../../../components/Summary.jsx';

import {
    createIncompleteAnnotationRenderer,
    StackedBar
} from '../../../components/charts/Bar.jsx';

import {
    useComputedCurationConfigValue,
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

function getNumericMetricData(request, result) {

    return {
        raw: _.last(_.last(result.data)),
        value: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded))),
        formatted: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated)))
    };
}

function EngagementTotalSummary({requests, results}) {

    let insight = null;
    const deltaRel = getNumericMetricData(requests.engagementTotalDeltaRel, results.engagementTotalDeltaRel);
    if (deltaRel.raw !== null) {
        insight = (
            <DeltaRelativeInsight
                formatted={deltaRel.formatted}
                value={deltaRel.raw}
            />
        );
    } else {
        insight = (
            <div className={`tlw-mt-1.5`}>
                {`--`}
            </div>
        );
    }

    return (
        <Summary
            description='The total number of engagements on TikTok.'
            title='Presence Engagement Total'
            insight={insight}
            value={<NumericalValue {...getNumericMetricData(requests.engagementTotal, results.engagementTotal)} />}
        />
    );
}

function Legend({data}) {
    const keys = ['likes', 'comments', 'shares'];
    const colors = ['tlw-text-sky-400', 'tlw-text-yellow-300', 'tlw-text-yellow-600'];
    const rows = _.map(keys, (key, idx) => {
        return (
            <tr className='tlw-text-sm'>
                <td>
                    <i className={`fa fa-fw fa-square ${colors[idx]}`} />
                    {_.capitalize(key)}
                </td>
                <td className='tlw-text-right'>
                    <strong className='tlw-ml-2'>{data[idx]}</strong>
                </td>
            </tr>
        );
    });

    return (
        <table>
            <tbody>
                {rows}
            </tbody>
        </table>
    );
}

function EngagementLegend({requests, results}) {

    const data = _.map([
        getNumericMetricData(requests.likes, results.likes),
        getNumericMetricData(requests.comments, results.comments),
        getNumericMetricData(requests.shares, results.shares)
    ], ({formatted}) => formatted);

    return (
        <Legend data={data} />
    );
}

function EngagementRateLineTooltip({lineConfig, chartConfigs, datum, placement}) {

    if (!datum) {
        return;
    }

    return (
        <RIQPopover
            triggerNode={<div/>}
            className='tlw-flex tlw-w-full tlw-items-center tlw-gap-[10px] tlw-text-md tlw-font-normal'
            placement={placement || 'top'}
            portalNode={document.body}
            additionalHookProps={{
                visible: true
            }}
        >
            <div>
                <div className='tlw-m-0 tlw-whitespace-nowrap tlw-rounded-t-md tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-md tlw-text-gray-700'>
                    {datum.tooltipTitle || datum.xValue}
                </div>

                <div className='tlw-py-4.5'>

                    {_.map(chartConfigs, (chartConfig, idx) => {
                        return (
                            <div
                                key={idx}
                                className='tlw-border-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-left even_tlw-border-l'
                            >
                                <Label>{chartConfig.chartName}</Label>
                                <div
                                    className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                    {chartConfig.metrics[0].expandedFormatter(datum[chartConfig.metrics[0].dataKey])}
                                </div>
                            </div>
                        );
                    })}
                </div>

            </div>
        </RIQPopover>
    );
}

function EngagementRateByInterval({interval = 'day', instanceId, requests, results}) {

    const dataKey = 'engagementRateByInterval';
    const dataByInterval = RIQMetricLineChart.collateMetricResults(interval, requests, results, [dataKey], null, 1);

    const chartConfigs = [{
        chartName: `Presence Engagement Rate per View`,
        metrics: [{
            dataKey,
            lineColor: '#6a788c',
            abbreviatedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(requests[dataKey], results[dataKey], RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
            expandedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(requests[dataKey], results[dataKey], RivalIQ.metrics.new.enums.MetricFormatTypes.expanded),
            metricRequest: requests[dataKey]
        }],
        yaxisProps: {
            allowDecimals: true
        }
    }];

    chartConfigs[0].tooltipContent = <EngagementRateLineTooltip
        chartConfigs={chartConfigs}
    />;

    return (
        <RIQMetricLineChart
            key='line-chart'
            chartKey={instanceId}
            dataByInterval={dataByInterval}
            chartConfigs={chartConfigs}
        />
    );
}

const ENGAGEMENT_TYPE_COLORS = [
    'rgb(56, 189, 248)',
    'rgb(253, 224, 71)',
    'rgb(202, 138, 4)'
];

function EngagementBarTooltip({payload}) {

    if (!payload || !payload.length) {
        return;
    }

    const placement = null;
    const datum = _.first(payload).payload;

    return (
        <RIQPopover
            triggerNode={<div/>}
            className='tlw-flex tlw-w-full tlw-items-center tlw-gap-[10px] tlw-text-md tlw-font-normal'
            placement={placement || 'top'}
            portalNode={document.body}
            additionalHookProps={{
                visible: true
            }}
        >
            <div>
                <div className='tlw-m-0 tlw-whitespace-nowrap tlw-rounded-t-md tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-md tlw-text-gray-700'>
                    {_.last(datum).title}
                </div>
                <div className='tlw-py-4.5'>
                    <div className='tlw-grid tlw-grid-cols-2 tlw-border-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-left even_tlw-border-l'>
                        <section className='tlw-border-[0] tlw-border-r tlw-border-solid tlw-border-gray-350 tlw-pr-4.5'>
                            <Label>Engagement Total</Label>
                            <div className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                {_.last(datum).engagement_total}
                            </div>
                        </section>
                        <section className='tlw-flex tlw-items-center tlw-pl-4.5'>
                            <Legend data={datum.slice(1, 4)} />
                        </section>
                    </div>
                </div>
            </div>
        </RIQPopover>
    );
}

function EngagementByInterval({interval = 'day', requests, results}) {

    const timezone = requests.engagementTotal.filters.timePeriod.timezone;

    const keys = [
        'likes',
        'comments',
        'shares'
    ];

    const data = _.map(
        _.groupBy(
            _.reduce(
                _.zip(
                    results.likesByInterval.data,
                    results.commentsByInterval.data,
                    results.sharesByInterval.data,
                    results.engagementTotalByInterval.data
                ),
                (memo, values) => memo.concat(_.map(values, _.tail)),
                []
            ),
            _.first
        ),
        (value, key, idx) => {

            const rawValues = _.map(value, _.last);
            const startDate = moment.tz(key, timezone);

            let title = startDate.format('MMM D, YYYY');
            if (interval !== 'day') {
                const endDate = moment.tz(startDate, timezone).endOf(interval);
                title = RivalIQ.metrics.new.MetricFormatter.formatDateInterval(startDate, endDate, timezone);
            }

            return [
                moment.tz(key, timezone).format('MM/DD'),
                ..._.take(rawValues, 3),
                _.some(keys, (k) => (
                    results.likesByInterval?.calculationMetadata?.domains?.[interval]?.[key]?.partialTimePeriod ||
                    results.commentsByInterval?.calculationMetadata?.domains?.[interval]?.[key]?.partialTimePeriod ||
                    results.sharesByInterval?.calculationMetadata?.domains?.[interval]?.[key]?.partialTimePeriod
                )),
                {
                    title,
                    engagement_total: _.last(rawValues)
                }
            ];
        }
    );

    const annotationRenderer = createIncompleteAnnotationRenderer({
        data,
        dataKey: 4
    });

    return (
        <>
            <Label>Presence Engagement Per {interval}</Label>
            <StackedBar
                colors={ENGAGEMENT_TYPE_COLORS}
                data={data}
                label={annotationRenderer}
                tooltip={EngagementBarTooltip}
                yAxisAllowDecimals={false}
            />
        </>
    );
}

export default function Engagements({instanceId, interval = 'day', pdfExportConfig, appSpecificExportConfig}) {
    const isPPTExport = useIsExportingPPT();
    const landscape = useContext(LandscapeContext);

    const {
        t
    } = useTranslation('apps/insights/components/Engagements');

    const timezone = useCurationConfigValue(instanceId, 'timezone');
    const companyId = useCurationConfigValue(instanceId, 'companyId');
    const dateRanges = useComputedCurationConfigValue(instanceId, 'dateRanges');

    const title = t('title');

    useSetExportConfig(instanceId, {
        title,
        supportedExportFormats: _.without(_.values(RivalIQ.enums.exportFormat), RivalIQ.enums.exportFormat.CSV),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig,
        appSpecificExportConfig
    });

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig.timePeriodStr
        }
    });

    const metricRequests = {
        engagementTotal: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: 'engagement_total',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                day: 'sum'
            }]
        },
        engagementTotalDeltaRel: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: {
                field: 'engagement_total',
                transform: 'deltaRelative',
                transformConfig: {
                    comparePeriodStartDate: dateRanges.compare.start,
                    comparePeriodEndDate: dateRanges.compare.end
                }
            },
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                day: 'sum'
            }]
        },
        engagementTotalByInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId', interval],
            measure: 'engagement_total',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        likes: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: 'likes',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                day: 'sum'
            }]
        },
        comments: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: 'comments',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                day: 'sum'
            }]
        },
        shares: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: 'shares',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                day: 'sum'
            }]
        },
        likesByInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId', interval],
            measure: 'likes',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        commentsByInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId', interval],
            measure: 'comments',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        sharesByInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId', interval],
            measure: 'shares',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        engagementRateByInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId', interval],
            measure: 'engagement_rate',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        }
    };

    return (
        <Panel
            instanceId={instanceId}
            title={title}
            exportSubtitle={appSpecificExportConfig.timePeriodStr}
            description={t('description')}
            showViewMore={false}
        >
            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => {

                        return (
                            <article>
                                <section className='tlw-flex tlw-border-[0] tlw-border-b tlw-border-solid tlw-border-gray-350 tlw-px-7.5 tlw-py-5'>
                                    <section className='tlw-border-[0] tlw-border-r tlw-border-solid tlw-border-gray-350 tlw-pr-7.5'>
                                        <EngagementTotalSummary
                                            requests={metricRequests}
                                            results={metricResults}
                                        />
                                    </section>
                                    <section className='tlw-flex tlw-items-center tlw-pl-7.5'>
                                        <EngagementLegend
                                            requests={metricRequests}
                                            results={metricResults}
                                        />
                                    </section>
                                </section>
                                <section className='tlw-border-[0] tlw-border-b tlw-border-solid tlw-border-gray-350 tlw-px-7.5 tlw-py-5'>
                                    <EngagementByInterval
                                        interval={interval}
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                </section>
                                <section className={isPPTExport ? 'tlw-break-before-page' : 'tlw-px-7.5 tlw-py-5'}>
                                    <EngagementRateByInterval
                                        instanceId={instanceId}
                                        interval={interval}
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                </section>
                            </article>
                        );
                    }
                }
            </MetricLoadingView>
        </Panel>
    );
}
